import React, { useState, useEffect, useCallback } from 'react';
import 'slick-carousel/slick/slick.css';
import style from './block.module.scss';
import { ReactComponent as Facebook } from '../assets/ic_facebook.svg';
import { ReactComponent as Instagram } from '../assets/ic_instagram.svg';
import { ReactComponent as TripAdvisor } from '../assets/ic_trip.svg';
import { ReactComponent as Youtube } from '../assets/ic_youtube.svg';
import { ReactComponent as Search } from './search.svg';
import { ReactComponent as Logo } from './logo-guedelon.svg';
import { GuedelonButton } from '../bouton-guedelon/button';
import { useMedia } from 'react-use';
import { ReactComponent as AddIcon } from './addIcon.svg';
import { Remove as RemoveIcon } from '@mui/icons-material';
import { ReactComponent as ArrowForwardIcon } from './arrow.svg';
import { ReactComponent as CloseIcon } from './close.svg';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useEditor } from '@smartfire/js-block-engine';
import apiFetch from '@wordpress/api-fetch';
import { styled } from '@mui/material/styles';
import { getLanguage, getLanguagePathPrefix, LANGUAGES } from '../shared/getLanguage';

type WPQueryResultItem = {
    id: number;
    type: string;
    subtype: string;
    url: string;
    title: string;
};

const CustomLinearProgress = styled(LinearProgress)(() => ({
    height: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: '#FFFFFF30',
    },
    [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: 'white',
    },
}));

export default function Header({ menu, menuButtons, socials, translatedLinks }) {
    const [open, setOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const isMobile = useMedia(`(max-width: ${style.tabletBreakpoint})`);
    const [openCategory, setOpenCategory] = useState('');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<{ pages: WPQueryResultItem[]; news: WPQueryResultItem[] }>({
        pages: [],
        news: [],
    });
    const [noresults, setNoresults] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const { isEditing } = useEditor();
    const [sticky, setSticky] = useState(false);
    const { t } = useTranslation();
    const currentLanguage = getLanguage();
    const navListener = useCallback(() => {
        let position = document.getElementById('navbarAnchor')?.getBoundingClientRect().top ?? 0;
        if (position <= -1 && !sticky) {
            setSticky(true);
        } else if (position > -1 && sticky) {
            setSticky(false);
        }
    }, [setSticky, sticky]);
    const clickListener = useCallback(
        (e) => {
            let anchor = open ? 'menuEndAnchor' : searching ? 'searchEndAnchor' : '';
            if (anchor !== '') {
                let position = document.getElementById(anchor)?.getBoundingClientRect().top;
                if (position && e.clientY > position) {
                    if (open) {
                        setOpen(false);
                    } else if (searching) {
                        setSearching(false);
                    }
                }
            }
        },
        [open, searching],
    );

    const handleLanguageChange = useCallback((lang) => {
        document.cookie = `wp-wpml_current_language=${lang}; path=/`; // Mettre à jour le cookie
        // i18n.changeLanguage(lang); // Mettre à jour la langue dans i18next sans recharger la page
    }, []);

    useEffect(() => {
        if (open || searching) {
            window.addEventListener('click', clickListener);
            return () => {
                window.removeEventListener('click', clickListener);
            };
        }
        return () => {};
    }, [clickListener, open, searching]);

    useEffect(() => {
        if (window) {
            //@ts-expect-error ts doesn't know about this custom variable
            if (window.smartfire) {
                //@ts-expect-error ts doesn't know about this custom variable
                window.smartfire.closeHeaderMenu = () => {
                    setOpen(false);
                    setSearching(false);
                };
            } else {
                //@ts-expect-error ts doesn't know about this custom variable
                window.smartfire = {
                    closeHeaderMenu: () => {
                        setOpen(false);
                        setSearching(false);
                    },
                };
            }
        }
    }, [setOpen, setSearching]);

    useEffect(() => {
        //initial listner call to set the header bar
        if (!isEditing) {
            navListener();
            window.addEventListener('scroll', navListener);
            window.addEventListener('resize', navListener);
            return () => {
                window.removeEventListener('scroll', navListener);
                window.removeEventListener('resize', navListener);
            };
        }
        return () => {};
    }, [navListener, isEditing]);

    useEffect(() => {
        let typingTimeout = setTimeout(() => {
            setNoresults(false);

            if (query !== '') {
                setWaiting(true);
                apiFetch<WPQueryResultItem[]>({
                    path: `wp/v2/search?search=${query}&type=post&subtype=post,page`,
                    method: 'GET',
                }).then((response) => {
                    setWaiting(false);
                    setResults({
                        pages: response.filter((post) => post.subtype === 'page'),
                        news: response.filter((post) => post.subtype === 'post'),
                    });
                    if (response.length === 0) {
                        setNoresults(true);
                    }
                });
            } else {
                setResults({ pages: [], news: [] });
            }
        }, 500);
        return () => {
            setWaiting(false);
            clearTimeout(typingTimeout);
        };
    }, [query]);

    return (
        <div className={sticky ? style.stickyBlock : style.block}>
            <div className={style.anchor} id="navbarAnchor" />
            <div className={style.content}>
                <div className={style.bar}>
                    <div className={style.container}>
                        <div
                            className={style.menuButton}
                            onClick={() => {
                                setOpen(!open);
                                setSearching(false);
                            }}
                        >
                            <div className={clsx(style.icon, open && style.iconOpen)} onClick={() => setOpen(!open)}>
                                <span className={style.iconLine}></span>
                            </div>

                            <div className={style.label}>{t('menu')}</div>
                        </div>
                        <div
                            className={style.searchButtonDesktop}
                            onClick={() => {
                                setOpen(false);
                                setSearching(!searching);
                            }}
                        >
                            <div className={style.icon}>
                                <Search />
                            </div>
                            <div className={style.label}>{t('rechercher')}</div>
                        </div>
                        <a href={getLanguagePathPrefix()} className={style.logo}>
                            <Logo />
                        </a>
                    </div>
                    <div className={style.container}>
                        {!isMobile && (
                            <div className={style.socials}>
                                {socials?.instagram && (
                                    <a target="_blank" rel="noreferrer noopener" href={socials.instagram}>
                                        <Instagram />
                                    </a>
                                )}
                                {socials?.facebook && (
                                    <a target="_blank" rel="noreferrer noopener" href={socials.facebook}>
                                        <Facebook />
                                    </a>
                                )}
                                {socials?.youtube && (
                                    <a target="_blank" rel="noreferrer noopener" href={socials.youtube}>
                                        <Youtube />
                                    </a>
                                )}
                                {socials?.trip && (
                                    <a target="_blank" rel="noreferrer noopener" href={socials.trip}>
                                        <TripAdvisor />
                                    </a>
                                )}
                            </div>
                        )}
                        <div className={style.languages}>
                            {LANGUAGES.length > 1 &&
                                LANGUAGES.map((language) => (
                                    <a
                                        href={
                                            language.lang === currentLanguage
                                                ? undefined
                                                : translatedLinks[language.label] ?? language.pathPrefix
                                        }
                                        key={language.lang}
                                        className={clsx(
                                            style.language,
                                            language.lang === currentLanguage && style.active,
                                        )}
                                        onClick={() => handleLanguageChange(language.lang)}
                                    >
                                        {language.label}
                                    </a>
                                ))}
                        </div>
                        {menuButtons &&
                            menuButtons.map((item) => (
                                <GuedelonButton
                                    key={item.guid}
                                    className={style.button}
                                    href={isEditing ? undefined : item.url}
                                    external={item.target === '_blank'}
                                >
                                    {item.title}
                                </GuedelonButton>
                            ))}

                        <div
                            className={style.searchButtonMobile}
                            onClick={() => {
                                setOpen(false);
                                setSearching(!searching);
                            }}
                        >
                            <div className={style.icon}>
                                <Search />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={
                        searching
                            ? isEditing
                                ? style.adminSearchPannel
                                : style.searchPannel
                            : isEditing
                            ? style.hidden
                            : style.hiddenSearchPannel
                    }
                >
                    {waiting ? (
                        <CustomLinearProgress sx={{ width: '100%' }} />
                    ) : (
                        <div className={style.loadPlaceholder} />
                    )}
                    <div
                        className={style.closeIcon}
                        onClick={() => {
                            setSearching(false);
                            setQuery('');
                        }}
                    >
                        <CloseIcon />
                    </div>
                    <div className={style.inputContainer}>
                        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} />
                        <div className={style.inputIcon}>
                            <Search />
                            {t('rechercher')}
                        </div>
                    </div>

                    {(results.pages.length > 0 || results.news.length > 0) && (
                        <div className={style.results}>
                            {results.pages.length > 0 && (
                                <>
                                    <h4>{t('pages')}:</h4>
                                    {results.pages.length > 0 &&
                                        results.pages.map((page) => (
                                            <div className={style.page} key={page.id}>
                                                <a href={page.url || undefined}>{page.title}</a>
                                            </div>
                                        ))}
                                </>
                            )}
                            {results.news.length > 0 && (
                                <>
                                    <h4>{t('actualités')}:</h4>
                                    {results.news.length > 0 &&
                                        results.news.map((page) => (
                                            <div className={style.page} key={page.id}>
                                                <a href={page.url || undefined}>{page.title}</a>
                                            </div>
                                        ))}
                                </>
                            )}
                        </div>
                    )}
                    {noresults && <div className={style.results}>{t('Aucun résultat')}</div>}
                    <div id={'searchEndAnchor'} />
                </div>
                <div
                    className={
                        open ? (isEditing ? style.adminMenu : style.menu) : isEditing ? style.hidden : style.hiddenMenu
                    }
                >
                    <div className={isMobile ? style.mobileButtons : style.hidden}>
                        {menuButtons &&
                            menuButtons.map((item) => (
                                <a
                                    className={style.button}
                                    key={item.guid}
                                    href={isEditing ? undefined : item.url}
                                    target={item.target}
                                    rel={item.target === '_blank' ? 'noopener, noreferer' : undefined}
                                >
                                    {item.title}
                                </a>
                            ))}
                    </div>
                    <div className={style.menuContent}>
                        <div className={style.column}>
                            <div className={style.container}>
                                {menu &&
                                    menu.length > 0 &&
                                    menu[0].children.map(
                                        (category) =>
                                            !(isMobile && category.title === '') && (
                                                <div
                                                    key={category.guid}
                                                    className={
                                                        isMobile && openCategory === category.title
                                                            ? style.openCategory
                                                            : style.category
                                                    }
                                                >
                                                    <a
                                                        className={style.categoryHeader}
                                                        onClick={
                                                            isMobile && category.children.length > 0
                                                                ? () => {
                                                                      openCategory === category.title
                                                                          ? setOpenCategory('')
                                                                          : setOpenCategory(category.title);
                                                                  }
                                                                : () => null
                                                        }
                                                        href={
                                                            (!(
                                                                category.attr_title &&
                                                                category.attr_title.includes('no link')
                                                            ) &&
                                                                category.url) ??
                                                            undefined
                                                        }
                                                        target={category.target}
                                                        rel={
                                                            category.target === '_blank'
                                                                ? 'noopener, noreferer'
                                                                : undefined
                                                        }
                                                    >
                                                        <span className={style.title}>{category.title}</span>
                                                        {isMobile &&
                                                            (category.children.length > 0 ? (
                                                                openCategory === category.title ? (
                                                                    <RemoveIcon className={style.extendButton} />
                                                                ) : (
                                                                    <AddIcon className={style.extendButton} />
                                                                )
                                                            ) : (
                                                                <span className={style.extendButton}>
                                                                    <ArrowForwardIcon className={style.extendButton} />
                                                                </span>
                                                            ))}
                                                    </a>
                                                    <div
                                                        className={
                                                            !isMobile || openCategory === category.title
                                                                ? style.items
                                                                : style.hiddenItems
                                                        }
                                                    >
                                                        {category.children &&
                                                            category.children.length > 0 &&
                                                            category.children.map((item) => (
                                                                <div key={item.guid}>
                                                                    {item.attr_title &&
                                                                        item.attr_title.includes('border top') && (
                                                                            <div className={style.spacer} />
                                                                        )}
                                                                    <div className={style.link}>
                                                                        <a
                                                                            href={item.url ?? undefined}
                                                                            rel={
                                                                                item.target === '_blank'
                                                                                    ? 'noopener, noreferer'
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            {item.title}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            ),
                                    )}
                            </div>
                        </div>

                        <div className={style.column}>
                            <div className={style.container}>
                                {menu &&
                                    menu.length > 1 &&
                                    menu[1].children.map(
                                        (category) =>
                                            !(isMobile && category.title === '') && (
                                                <div
                                                    className={
                                                        isMobile && openCategory === category.title
                                                            ? style.openCategory
                                                            : style.category
                                                    }
                                                >
                                                    <a
                                                        className={style.categoryHeader}
                                                        onClick={
                                                            isMobile && category.children.length > 0
                                                                ? () => {
                                                                      openCategory === category.title
                                                                          ? setOpenCategory('')
                                                                          : setOpenCategory(category.title);
                                                                  }
                                                                : () => null
                                                        }
                                                        href={
                                                            (!(
                                                                category.attr_title &&
                                                                category.attr_title.includes('no link')
                                                            ) &&
                                                                category.url) ??
                                                            undefined
                                                        }
                                                        target={category.target}
                                                        rel={
                                                            category.target === '_blank'
                                                                ? 'noopener, noreferer'
                                                                : undefined
                                                        }
                                                    >
                                                        <span className={style.title}>{category.title}</span>
                                                        {isMobile &&
                                                            (category.children.length > 0 ? (
                                                                openCategory === category.title ? (
                                                                    <RemoveIcon className={style.extendButton} />
                                                                ) : (
                                                                    <AddIcon className={style.extendButton} />
                                                                )
                                                            ) : (
                                                                <span className={style.extendButton}>
                                                                    <ArrowForwardIcon className={style.extendButton} />
                                                                </span>
                                                            ))}
                                                    </a>
                                                    <div
                                                        className={
                                                            !isMobile || openCategory === category.title
                                                                ? style.items
                                                                : style.hiddenItems
                                                        }
                                                    >
                                                        {category.children &&
                                                            category.children.length > 0 &&
                                                            category.children.map((item) => (
                                                                <div key={item.guid}>
                                                                    {item.attr_title &&
                                                                        item.attr_title.includes('border top') && (
                                                                            <div className={style.spacer} />
                                                                        )}
                                                                    <div className={style.link}>
                                                                        <a
                                                                            href={item.url ?? undefined}
                                                                            target={item.target}
                                                                            rel={
                                                                                item.target === '_blank'
                                                                                    ? 'noopener, noreferer'
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            {item.title}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            ),
                                    )}
                            </div>
                        </div>
                        <div className={style.column}>
                            <div className={style.container}>
                                {menu &&
                                    menu.length > 2 &&
                                    menu[2].children.map(
                                        (category) =>
                                            !(isMobile && category.title === '') && (
                                                <div
                                                    className={
                                                        isMobile && openCategory === category.title
                                                            ? style.openCategory
                                                            : style.category
                                                    }
                                                >
                                                    <a
                                                        className={style.categoryHeader}
                                                        onClick={
                                                            isMobile && category.children.length > 0
                                                                ? () => {
                                                                      openCategory === category.title
                                                                          ? setOpenCategory('')
                                                                          : setOpenCategory(category.title);
                                                                  }
                                                                : () => null
                                                        }
                                                        href={
                                                            (!(
                                                                category.attr_title &&
                                                                category.attr_title.includes('no link')
                                                            ) &&
                                                                category.url) ??
                                                            undefined
                                                        }
                                                        target={category.target}
                                                        rel={
                                                            category.target === '_blank'
                                                                ? 'noopener, noreferer'
                                                                : undefined
                                                        }
                                                    >
                                                        <span className={style.title}>{category.title}</span>
                                                        {isMobile &&
                                                            (category.children.length > 0 ? (
                                                                openCategory === category.title ? (
                                                                    <RemoveIcon className={style.extendButton} />
                                                                ) : (
                                                                    <AddIcon className={style.extendButton} />
                                                                )
                                                            ) : (
                                                                <span className={style.extendButton}>
                                                                    <ArrowForwardIcon className={style.extendButton} />
                                                                </span>
                                                            ))}
                                                    </a>
                                                    <div
                                                        className={
                                                            !isMobile || openCategory === category.title
                                                                ? style.items
                                                                : style.hiddenItems
                                                        }
                                                    >
                                                        {category.children &&
                                                            category.children.length > 0 &&
                                                            category.children.map((item) => (
                                                                <div key={item.guid}>
                                                                    {item.attr_title &&
                                                                        item.attr_title.includes('border top') && (
                                                                            <div className={style.spacer} />
                                                                        )}
                                                                    <div className={style.link}>
                                                                        <a
                                                                            href={item.url ?? undefined}
                                                                            target={item.target}
                                                                            rel={
                                                                                item.target === '_blank'
                                                                                    ? 'noopener, noreferer'
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            {item.title}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            ),
                                    )}
                            </div>
                        </div>

                        {isMobile && (
                            <div className={style.socials}>
                                <div className={style.socials}>
                                    {socials?.instagram && (
                                        <a target="_blank" rel="noreferrer noopener" href={socials.instagram}>
                                            <Instagram />
                                        </a>
                                    )}
                                    {socials?.facebook && (
                                        <a target="_blank" rel="noreferrer noopener" href={socials.facebook}>
                                            <Facebook />
                                        </a>
                                    )}
                                    {socials?.youtube && (
                                        <a target="_blank" rel="noreferrer noopener" href={socials.youtube}>
                                            <Youtube />
                                        </a>
                                    )}
                                    {socials?.trip && (
                                        <a target="_blank" rel="noreferrer noopener" href={socials.trip}>
                                            <TripAdvisor />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div id={'menuEndAnchor'} />
                </div>
            </div>
        </div>
    );
}
